import { useCallback, useMemo, useState } from 'react';

import { dayOf, describe } from '../../dates';
import { useLanguage } from '../../contexts/language';

import { Initials } from '../initials';

import styles from './style.module.css';

// The question an open invitation asks, as drawn in the blueprint: who wants
// to connect, what they can do once connected, that accepting shares the
// whole diary, entries already written included, while the journal stays
// private (C22, C38), and Accept or Decline. onAnswer is given the
// choice and settles once it is taken; when it fails, the reason shows under
// the buttons and both can be pressed again.
export function InvitationAsk({ invitation, onAnswer }) {
  const { text, language, translate } = useLanguage();
  const [ busy, setBusy ] = useState(false);
  const [ error, setError ] = useState();
  const { email, sentAt, clinician } = invitation;
  const name = `${ clinician.firstName } ${ clinician.lastName }`;

  const choose = useCallback(function({ currentTarget }) {
    setBusy(true);
    setError();

    return onAnswer(currentTarget.value === 'accept').catch(function(failed) {
      setBusy(false);
      setError(failed.message);
    });
  }, [ onAnswer ]);

  const abilities = useMemo(function() {
    return [ text.canReadDiary, text.canPlanSessions, text.canAssignQuestionnaires, text.canWriteNotes ].map(function(ability) {
      return <li key={ ability }>{ ability }</li>;
    });
  }, [ text ]);

  const message = useMemo(function() {
    if (error == null) {
      return null;
    }

    return <span className={ styles.error } role='alert'>{ translate(error) }</span>;
  }, [ error, translate ]);

  return (
    <>
      <span className={ styles.kicker }>{ text.invitation }</span>

      <section className={ styles.card }>
        <div className={ styles.who }>
          <Initials firstName={ clinician.firstName } lastName={ clinician.lastName } large/>

          <h1 className={ styles.title }>{ text.wantsToConnectWithYou.replace('{name}', name) }</h1>
        </div>

        <div className={ styles.can }>
          <span className={ styles.label }>{ text.ifYouAccept.replace('{name}', name) }</span>

          <ul className={ styles.abilities }>{ abilities }</ul>
        </div>

        <div className={ styles.private }>
          <span className={ styles.label }>{ text.journalStaysPrivate }</span>
          <span>{ text.acceptingSharesDiary.replace('{name}', name) }</span>
        </div>

        <div className={ styles.actions }>
          <button className={ styles.accept } type='button' value='accept' disabled={ busy } onClick={ choose }>{ text.accept }</button>
          <button className={ styles.decline } type='button' value='decline' disabled={ busy } onClick={ choose }>{ text.decline }</button>
        </div>

        { message }
      </section>

      <p className={ styles.sent }>{ text.sentToOn.replace('{email}', email).replace('{date}', describe({ day: dayOf(sentAt), language, short: true })) }</p>
    </>
  );
}
